/**
 * Pointy-top axial hex maths.
 *
 * Conventions (following Amit Patel's hex reference):
 *   - axial coords `{q, r}`, implied cube `s = -q - r`
 *   - pointy-top orientation, so neighbours are E/W and the four diagonals
 *   - `HexKey` is `"q,r"` — a string so hexes can live in plain object maps and
 *     survive `JSON.stringify` for save games
 */

import type { Axial, HexKey, Point } from './types';

/** Neighbour offsets in clockwise order starting from East. */
export const HEX_DIRECTIONS: readonly Axial[] = [
  { q: 1, r: 0 },   // 0 E
  { q: 1, r: -1 },  // 1 NE
  { q: 0, r: -1 },  // 2 NW
  { q: -1, r: 0 },  // 3 W
  { q: -1, r: 1 },  // 4 SW
  { q: 0, r: 1 },   // 5 SE
];

/** Human-facing direction labels, index-matched to HEX_DIRECTIONS. */
export const HEX_DIRECTION_NAMES: readonly string[] = ['E', 'NE', 'NW', 'W', 'SW', 'SE'];

export function key(coord: Axial): HexKey {
  return `${coord.q},${coord.r}`;
}

export function parseKey(k: HexKey): Axial {
  const comma = k.indexOf(',');
  return { q: Number(k.slice(0, comma)), r: Number(k.slice(comma + 1)) };
}

export function equals(a: Axial, b: Axial): boolean {
  return a.q === b.q && a.r === b.r;
}

export function add(a: Axial, b: Axial): Axial {
  return { q: a.q + b.q, r: a.r + b.r };
}

export function subtract(a: Axial, b: Axial): Axial {
  return { q: a.q - b.q, r: a.r - b.r };
}

export function scale(a: Axial, factor: number): Axial {
  return { q: a.q * factor, r: a.r * factor };
}

export function neighbor(coord: Axial, direction: number): Axial {
  // Modulo keeps callers from having to normalise a rotating direction index.
  const dir = HEX_DIRECTIONS[((direction % 6) + 6) % 6]!;
  return add(coord, dir);
}

export function neighbors(coord: Axial): Axial[] {
  return HEX_DIRECTIONS.map((d) => add(coord, d));
}

/** Neighbour keys — the common case, avoiding an intermediate object array. */
export function neighborKeys(coord: Axial): HexKey[] {
  const out: HexKey[] = [];
  for (const d of HEX_DIRECTIONS) out.push(`${coord.q + d.q},${coord.r + d.r}`);
  return out;
}

/** Hex distance: the cube-coordinate Chebyshev-ish metric. */
export function distance(a: Axial, b: Axial): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  const ds = -dq - dr;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(ds));
}

/** Ring distance from the origin — used to classify Core / Mid Rim / Outer Rim. */
export function ringOf(coord: Axial): number {
  return distance(coord, { q: 0, r: 0 });
}

/** Every hex within `radius` of the origin, ordered by ring then clockwise. */
export function spiral(radius: number): Axial[] {
  const out: Axial[] = [{ q: 0, r: 0 }];
  for (let ring = 1; ring <= radius; ring++) {
    // Start at the SW corner so the walk comes out clockwise from due East.
    let coord = add({ q: 0, r: 0 }, scale(HEX_DIRECTIONS[4]!, ring));
    for (let side = 0; side < 6; side++) {
      for (let step = 0; step < ring; step++) {
        out.push(coord);
        coord = neighbor(coord, side);
      }
    }
  }
  return out;
}

/** Just the hexes exactly `radius` out from the origin. */
export function ring(radius: number): Axial[] {
  if (radius <= 0) return [{ q: 0, r: 0 }];
  const out: Axial[] = [];
  let coord = scale(HEX_DIRECTIONS[4]!, radius);
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < radius; step++) {
      out.push(coord);
      coord = neighbor(coord, side);
    }
  }
  return out;
}

/* ------------------------------------------------------------------ pixel space */

/**
 * Axial → pixel centre, pointy-top.
 * `size` is the hex's circumradius (centre to corner).
 */
export function toPixel(coord: Axial, size: number): Point {
  const x = size * Math.sqrt(3) * (coord.q + coord.r / 2);
  const y = size * 1.5 * coord.r;
  return { x, y };
}

/** Pixel → nearest axial. Rounds in cube space so ties break correctly. */
export function fromPixel(point: Point, size: number): Axial {
  const q = ((Math.sqrt(3) / 3) * point.x - (1 / 3) * point.y) / size;
  const r = ((2 / 3) * point.y) / size;
  return roundAxial(q, r);
}

/** Round fractional axial coords to the nearest hex via cube rounding. */
export function roundAxial(qf: number, rf: number): Axial {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);

  // Cube coords must sum to zero; fix up whichever axis we rounded furthest.
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;

  return { q, r };
}

/** The six corner points of a pointy-top hex, for canvas paths. */
export function corners(center: Point, size: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < 6; i++) {
    // -90° offset puts the first corner at the top, which is what "pointy-top" means.
    const angle = (Math.PI / 180) * (60 * i - 90);
    out.push({ x: center.x + size * Math.cos(angle), y: center.y + size * Math.sin(angle) });
  }
  return out;
}

/** Straight line of hexes between two coords, inclusive of both ends. */
export function lineBetween(a: Axial, b: Axial): Axial[] {
  const n = distance(a, b);
  if (n === 0) return [{ ...a }];
  const out: Axial[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    out.push(roundAxial(a.q + (b.q - a.q) * t, a.r + (b.r - a.r) * t));
  }
  return out;
}

/**
 * Which of the six directions best matches a stick vector.
 *
 * The input layer snaps analog sticks to hex neighbours, and the renderer uses the same
 * function for mouse-drag hints, so the mapping lives here rather than in either.
 * Pointy-top neighbours sit at 0°, 60°, 120°, 180°, 240°, 300° measured in *pixel*
 * space, which is why we compare against `toPixel` of each direction.
 */
export function directionFromVector(dx: number, dy: number): number {
  let best = 0;
  let bestDot = -Infinity;
  const len = Math.hypot(dx, dy) || 1;
  const nx = dx / len;
  const ny = dy / len;

  for (let i = 0; i < 6; i++) {
    const p = toPixel(HEX_DIRECTIONS[i]!, 1);
    const pl = Math.hypot(p.x, p.y) || 1;
    const dot = nx * (p.x / pl) + ny * (p.y / pl);
    if (dot > bestDot) {
      bestDot = dot;
      best = i;
    }
  }
  return best;
}

/**
 * Breadth-first reachable set within `maxSteps`, honouring a passability predicate.
 * Returns a map of key → steps taken. Used for fleet move range and AI pathing.
 */
export function reachable(
  from: Axial,
  maxSteps: number,
  passable: (k: HexKey) => boolean,
): Map<HexKey, number> {
  const seen = new Map<HexKey, number>([[key(from), 0]]);
  let frontier: Axial[] = [from];

  for (let step = 1; step <= maxSteps; step++) {
    const next: Axial[] = [];
    for (const coord of frontier) {
      for (const d of HEX_DIRECTIONS) {
        const n = { q: coord.q + d.q, r: coord.r + d.r };
        const nk = key(n);
        if (seen.has(nk) || !passable(nk)) continue;
        seen.set(nk, step);
        next.push(n);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return seen;
}

/**
 * Shortest path between two hexes through passable terrain, or `null` if unreachable.
 * Inclusive of `to`, exclusive of `from` — i.e. the list of jumps to make.
 */
export function findPath(
  from: Axial,
  to: Axial,
  maxSteps: number,
  passable: (k: HexKey) => boolean,
): HexKey[] | null {
  const startKey = key(from);
  const goalKey = key(to);
  if (startKey === goalKey) return [];

  const cameFrom = new Map<HexKey, HexKey>();
  const seen = new Set<HexKey>([startKey]);
  let frontier: Axial[] = [from];

  for (let step = 1; step <= maxSteps; step++) {
    const next: Axial[] = [];
    for (const coord of frontier) {
      const ck = key(coord);
      for (const d of HEX_DIRECTIONS) {
        const n = { q: coord.q + d.q, r: coord.r + d.r };
        const nk = key(n);
        if (seen.has(nk) || !passable(nk)) continue;
        seen.add(nk);
        cameFrom.set(nk, ck);

        if (nk === goalKey) {
          // Walk the parent chain back and reverse it.
          const path: HexKey[] = [];
          let cur: HexKey | undefined = nk;
          while (cur && cur !== startKey) {
            path.push(cur);
            cur = cameFrom.get(cur);
          }
          return path.reverse();
        }
        next.push(n);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return null;
}
